
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Loader2, Check, ArrowUpRight, ArrowDownRight } from "lucide-react"
import { toast } from "sonner"
import { useAuth } from "@/lib/auth/auth-context"
import { useTransactions } from "@/lib/context/transaction-context"
import { supabase } from "@/integrations/supabase/client"

interface Transaction {
  amount: string
  description: string
  category: string
  type: "expense" | "income"
  date: string
}

interface StatementTransactionsProps {
  transactions: Transaction[]
  onComplete: () => void
}

export function StatementTransactions({ transactions, onComplete }: StatementTransactionsProps) {
  const { user } = useAuth()
  const { refreshTransactions } = useTransactions()
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    new Set(transactions.map((_, i) => i))
  )
  const [editedTransactions, setEditedTransactions] = useState<Transaction[]>(transactions)
  const [isSaving, setIsSaving] = useState(false)

  const toggleSelection = (index: number) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(index)) {
      newSelected.delete(index)
    } else {
      newSelected.add(index)
    }
    setSelectedIds(newSelected)
  }

  const toggleAll = () => {
    if (selectedIds.size === transactions.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(transactions.map((_, i) => i)))
    }
  }

  const updateTransaction = (index: number, field: keyof Transaction, value: string) => {
    const updated = [...editedTransactions]
    updated[index] = { ...updated[index], [field]: value }
    setEditedTransactions(updated)
  }

  const handleImport = async () => {
    if (selectedIds.size === 0) {
      toast.error("Please select at least one transaction to import")
      return
    }

    if (!user) {
      toast.error("You must be logged in to import transactions")
      return
    }

    setIsSaving(true)

    try {
      const selectedTransactions = Array.from(selectedIds).map((i) => editedTransactions[i])

      // Group by type
      const expenses = selectedTransactions.filter((t) => t.type === "expense")
      const incomes = selectedTransactions.filter((t) => t.type === "income")

      let successCount = 0

      // Insert expenses
      if (expenses.length > 0) {
        const expenseData = expenses.map((t) => ({
          user_id: user.id,
          description: t.description,
          amount: parseFloat(t.amount),
          category: t.category,
          date: t.date
        }))

        const { error: expenseError } = await supabase.from("user_expenses").insert(expenseData)

        if (expenseError) {
          console.error("Error inserting expenses:", expenseError)
          toast.error(`Failed to import some expenses: ${expenseError.message}`)
        } else {
          successCount += expenses.length
        }
      }

      // Insert incomes
      if (incomes.length > 0) {
        const incomeData = incomes.map((t) => ({
          user_id: user.id,
          description: t.description,
          amount: parseFloat(t.amount),
          date: t.date
        }))

        const { error: incomeError } = await supabase.from("user_incomes").insert(incomeData)

        if (incomeError) {
          console.error("Error inserting incomes:", incomeError)
          toast.error(`Failed to import some incomes: ${incomeError.message}`)
        } else {
          successCount += incomes.length
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} transaction${successCount !== 1 ? "s" : ""}!`)
        await refreshTransactions()
        onComplete()
      }
    } catch (error) {
      console.error("Import error:", error)
      toast.error("Failed to import transactions. Please try again.")
    } finally {
      setIsSaving(false)
    }
  }

  const selectedCount = selectedIds.size
  const totalIncome = editedTransactions
    .filter((t, i) => selectedIds.has(i) && t.type === "income")
    .reduce((sum, t) => sum + parseFloat(t.amount), 0)
  const totalExpense = editedTransactions
    .filter((t, i) => selectedIds.has(i) && t.type === "expense")
    .reduce((sum, t) => sum + parseFloat(t.amount), 0)

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Review Transactions</h3>
          <p className="text-sm text-muted-foreground">
            {selectedCount} of {transactions.length} selected
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={toggleAll}
        >
          {selectedIds.size === transactions.length ? "Deselect All" : "Select All"}
        </Button>
      </div>

      {/* Summary */}
      <Card className="p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Total Income</p>
            <p className="text-lg font-bold text-income">+${totalIncome.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total Expenses</p>
            <p className="text-lg font-bold text-expense">-${totalExpense.toFixed(2)}</p>
          </div>
        </div>
      </Card>

      {/* Transaction List */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {editedTransactions.map((transaction, index) => {
          const isSelected = selectedIds.has(index)
          const isIncome = transaction.type === "income"

          return (
            <Card
              key={index}
              className={`p-3 transition-all ${
                isSelected ? "ring-2 ring-primary" : "opacity-60"
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Checkbox */}
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleSelection(index)}
                  className="mt-1"
                />

                {/* Icon */}
                <div
                  className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                    isIncome ? "bg-income/10" : "bg-expense/10"
                  }`}
                >
                  {isIncome ? (
                    <ArrowDownRight className="h-5 w-5 text-income" />
                  ) : (
                    <ArrowUpRight className="h-5 w-5 text-expense" />
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 space-y-2">
                  <Input
                    value={transaction.description}
                    onChange={(e) => updateTransaction(index, "description", e.target.value)}
                    className="h-8 text-sm font-medium"
                    disabled={isSaving}
                  />

                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      value={transaction.amount}
                      onChange={(e) => updateTransaction(index, "amount", e.target.value)}
                      className="h-8 text-sm"
                      placeholder="Amount"
                      disabled={isSaving}
                    />
                    <Input
                      value={transaction.category}
                      onChange={(e) => updateTransaction(index, "category", e.target.value)}
                      className="h-8 text-sm"
                      placeholder="Category"
                      disabled={isSaving}
                    />
                    <Input
                      type="date"
                      value={transaction.date}
                      onChange={(e) => updateTransaction(index, "date", e.target.value)}
                      className="h-8 text-sm"
                      disabled={isSaving}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        isIncome ? "bg-income/10 text-income" : "bg-expense/10 text-expense"
                      }`}
                    >
                      {isIncome ? "Income" : "Expense"}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          )
        })}
      </div>

      {/* Import Button */}
      <Button
        type="button"
        onClick={handleImport}
        disabled={isSaving || selectedIds.size === 0}
        className="w-full bg-accent hover:bg-accent/90 text-accent-foreground h-12"
      >
        {isSaving ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Importing...
          </>
        ) : (
          <>
            <Check className="h-4 w-4 mr-2" />
            Import {selectedCount} Transaction{selectedCount !== 1 ? "s" : ""}
          </>
        )}
      </Button>
    </div>
  )
}
